import clsx from "clsx";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { useDispatch } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";
import { TextField } from "src/ui/shared/form/TextField";
import LanguageSwitcher from "src/ui/shared/LanguageSwitcher/LanguageSwitcher";
import { copyToClipboard } from "src/ui/utils/clipboard";

import { useClaimStarterSubscriptionMutation, useLazyGetWorkspacesQuery } from "@dashboard/workspaces/api";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import { showSuccessToast } from "@shared/toasts/Toasts";

import createWorkspaceImage from "../../assets/CreateWorkspace_400h.webp";
import inviteToWorkspaceImage from "../../assets/InviteToWorkspace_400h.webp";

import { useWorkspaceClaimStyles } from "./WorkspaceClaimSection.styles";

type WorkspaceClaimOptionCardProps = {
    isSelected: boolean;
    onSelect: () => void;
    classes: ReturnType<typeof useWorkspaceClaimStyles>["classes"];
};

const JoinWorkspaceCard = ({ isSelected, onSelect, classes }: WorkspaceClaimOptionCardProps) => {
    const { t } = useTranslation(["common"]);
    return (
        <Box
            role="button"
            tabIndex={0}
            className={clsx(classes.optionCard, isSelected && classes.optionCardSelected)}
            onClick={onSelect}
        >
            <Box className={classes.optionImageWrapper}>
                <Chip label={t("common:section.tutorial.badge.enterprise")} size="small" className={classes.badgeEnterprise} />
                <Box className={clsx(classes.indicator, isSelected && classes.indicatorSelected)} aria-hidden>
                    {isSelected ? <i className="fa-solid fa-check" /> : null}
                </Box>
                <img src={inviteToWorkspaceImage} alt="" className={classes.optionImage} />
            </Box>
            <Box className={classes.optionBody}>
                <Typography variant="subtitle1" className={classes.optionTitle}>
                    {t("common:section.tutorial.button.joinExistingWorkspace")}
                </Typography>
                <Typography variant="body2" className={classes.optionDescription}>
                    {t("common:section.tutorial.description.joinWorkspace")}
                </Typography>
            </Box>
        </Box>
    );
};

const CreateWorkspaceCard = ({ isSelected, onSelect, classes }: WorkspaceClaimOptionCardProps) => {
    const { t } = useTranslation(["common"]);
    return (
        <Box
            role="button"
            tabIndex={0}
            className={clsx(classes.optionCard, isSelected && classes.optionCardSelected)}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect();
                }
            }}
        >
            <Box className={classes.optionImageWrapper}>
                <Chip label={t("common:section.tutorial.badge.free")} size="small" className={classes.badge} />
                <Box className={clsx(classes.indicator, isSelected && classes.indicatorSelected)} aria-hidden>
                    {isSelected ? <i className="fa-solid fa-check" /> : null}
                </Box>
                <img src={createWorkspaceImage} alt="" className={classes.optionImage} />
            </Box>
            <Box className={classes.optionBody}>
                <Typography variant="subtitle1" className={classes.optionTitle}>
                    {t("common:section.tutorial.button.createFreeWorkspace")}
                </Typography>
                <Typography variant="body2" className={classes.optionDescription}>
                    {t("common:section.tutorial.description.createWorkspace")}
                </Typography>
            </Box>
        </Box>
    );
};

type FormValues = {
    action: "join" | "create";
    email: string;
};

export const WorkspaceClaimSection = () => {
    const { t } = useTranslation(["common"]);
    const { classes } = useWorkspaceClaimStyles();
    const user = AuthService.getCurrentUser();
    const dispatch = useDispatch();
    const [claimStarterSubscription] = useClaimStarterSubscriptionMutation();
    const [getWorkspaces] = useLazyGetWorkspacesQuery({});

    const onFreePlanConfirm = async () => {
        await claimStarterSubscription().unwrap();
        const workspaces = await getWorkspaces({}).unwrap();
        const workspaceId = workspaces[0].id;
        dispatch(setCurrentWorkspaceId(workspaceId));
        showSuccessToast(t("common:section.tutorial.toast.starterSubscriptionClaimSuccess"));
    };

    return (
        <Box className={classes.container}>
            <Form<FormValues>
                onSubmit={() => undefined}
                initialValues={{ action: "create", email: user?.email || "" }}
                render={({ form, handleSubmit, values }) => (
                    <Box component="form" onSubmit={handleSubmit} className={classes.content}>
                        <Box className={classes.titleContainer}>
                            <Box className={classes.languageSwitcher}>
                                <LanguageSwitcher fullWidth />
                            </Box>
                        </Box>
                        <Typography variant="body1" className={classes.description}>
                            {t("common:section.tutorial.description.yourWorkspaceCta")}
                        </Typography>

                        <Box className={classes.cardList}>
                            <CreateWorkspaceCard
                                isSelected={values?.action === "create"}
                                onSelect={() => form.change("action", "create")}
                                classes={classes}
                            />
                            <JoinWorkspaceCard
                                isSelected={values?.action === "join"}
                                onSelect={() => form.change("action", "join")}
                                classes={classes}
                            />
                        </Box>

                        <Box className={classes.dynamicSection}>
                            {values?.action === "join" ? (
                                <Paper className={classes.detailPanel} elevation={0}>
                                    <CustomAlert severity="info">{t("common:section.tutorial.alert.getStarted")}</CustomAlert>
                                    <Box className={classes.inputBox}>
                                        <Box className={classes.emailField}>
                                            <TextField
                                                name="email"
                                                label={t("common:section.tutorial.input.email.caption")}
                                                fullWidth
                                                disabled
                                            />
                                        </Box>
                                        <PrimaryButton
                                            size="medium"
                                            onClick={() => void copyToClipboard(values?.email)}
                                            startIcon={
                                                <Box component="span">
                                                    <i className="fa-regular fa-copy" />
                                                </Box>
                                            }
                                        >
                                            {t("common:button.copy")}
                                        </PrimaryButton>
                                    </Box>
                                </Paper>
                            ) : (
                                <Box className={classes.actionCenter}>
                                    <PrimaryButton onClick={onFreePlanConfirm}>
                                        {t("common:section.tutorial.button.startFreePlanNow")}
                                    </PrimaryButton>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            />
        </Box>
    );
};
